/**
 * PromoterDashboardStats.tsx - Promoter Performance Statistics Component
 *
 * This component displays key performance statistics for individual promoters
 * with visual indicators, progress tracking, and comparative metrics.
 *
 * Key Features:
 * - Individual promoter performance metrics
 * - Visual progress indicators and charts
 * - Comparative statistics with other promoters
 * - Goal achievement tracking and progress
 * - Performance trends and analytics
 * - Achievement milestones and badges
 *
 * Statistics Displayed:
 * - Total Amount Raised: Cumulative donations attributed
 * - Number of Donations: Count of donations from promoter efforts
 * - Average Donation: Mean donation amount from promoter
 * - Goal Progress: Percentage toward individual goal
 * - Ranking: Position among all promoters
 * - Performance Rate: Donations per day/week metrics
 *
 * Visual Elements:
 * - Progress bars with goal indicators
 * - Trend arrows showing performance direction
 * - Achievement badges for milestones
 * - Color-coded performance indicators
 * - Comparative charts and benchmarks
 *
 * Props:
 * - promoterId: string - ID of promoter for statistics
 * - showComparison?: boolean - Whether to show comparative metrics
 *
 * Dependencies: MobX store, statistics calculations, chart components
 */

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { DollarSign, Users, BarChart3, Hash, UserPlus } from "lucide-react";
import { fundraiserStore } from "../../stores/FundraiserStore";
import {
  FundraisersDonationsBoardResponse,
  PromoterProgressModel,
} from "../../models";

const ContentCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f5f9;
  transition: transform 0.2s ease;
  opacity: 0;
  animation: statCardFadeInPromoter 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;

  &:nth-child(1) {
    animation-delay: 0.1s;
  }
  &:nth-child(2) {
    animation-delay: 0.2s;
  }
  &:nth-child(3) {
    animation-delay: 0.3s;
  }
  &:nth-child(4) {
    animation-delay: 0.4s;
  }

  @keyframes statCardFadeInPromoter {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &:hover {
    transform: translateY(-2px);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const StatIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
`;

const StatTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 8px;
`;

const StatChange = styled.div<{ positive: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.positive ? "#10b981" : "#ef4444")};
`;

interface PromoterDashboardStatsProps {
  promoterId: string;
  fundraiserId: string;
  promoter: PromoterProgressModel;
  promoters: PromoterProgressModel[];
  donations: FundraisersDonationsBoardResponse[];
}

export const PromoterDashboardStats: React.FC<PromoterDashboardStatsProps> =
  observer(({ promoterId, fundraiserId, promoter, promoters, donations }) => {
    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    // Calculate stats for the promoter
    const allPromoters = promoters; //fundraiserStore.getFundraiserPromoters(fundraiserId);
    //const donations = fundraiserStore.getPromoterDonations(promoterId, fundraiserId);
    const totalRaised = donations.reduce((sum, d) => sum + d.amount, 0);
    const donationCount = donations.length;
    const averageDonation = donationCount > 0 ? totalRaised / donationCount : 0;
    const activePromoters = allPromoters.filter((p) => p.isActive).length;

    return (
      <ContentCard>
        <StatsGrid>
          <StatCard>
            <StatHeader>
              <StatIcon color="#10b981">
                <DollarSign size={24} />
              </StatIcon>
              <StatTitle>Total Raised</StatTitle>
            </StatHeader>
            <StatValue>{formatCurrency(totalRaised)}</StatValue>
            <StatChange positive={true}>
              {totalRaised > 0
                ? ((totalRaised / 25000) * 100).toFixed(1) + "% of goal"
                : "No donations yet"}
            </StatChange>
          </StatCard>

          <StatCard>
            <StatHeader>
              <StatIcon color="#f59e0b">
                <Hash size={24} />
              </StatIcon>
              <StatTitle>Donations</StatTitle>
            </StatHeader>
            <StatValue>{donationCount}</StatValue>
            <StatChange positive={true}>Total contributions</StatChange>
          </StatCard>

          <StatCard>
            <StatHeader>
              <StatIcon color="#3b82f6">
                <BarChart3 size={24} />
              </StatIcon>
              <StatTitle>Average</StatTitle>
            </StatHeader>
            <StatValue>{formatCurrency(averageDonation)}</StatValue>
            <StatChange positive={true}>Per donation</StatChange>
          </StatCard>

          <StatCard>
            <StatHeader>
              <StatIcon color="#8b5cf6">
                <UserPlus size={24} />
                {/* <img
                  src="/user-plus.svg"
                  alt="Promoters"
                  width={24}
                  height={24}
                  style={{
                    filter:
                      "brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(7500%) hue-rotate(120deg) brightness(103%) contrast(103%)",
                  }}
                /> */}
              </StatIcon>
              <StatTitle>Promoters</StatTitle>
            </StatHeader>
            <StatValue>{promoters.length}</StatValue>
            <StatChange positive={activePromoters > 0}>
              Active promoters
            </StatChange>
          </StatCard>
        </StatsGrid>
      </ContentCard>
    );
  });
